import React, { Fragment, useReducer, useState } from 'react'
import * as R from 'ramda'
import styled, { createGlobalStyle } from 'styled-components'
import { Button } from 'semantic-ui-react'

import { FilterDropdown } from 'components/FilterDropdown'
import { FilterItem } from 'components/FilterItem'
import { LinkButton } from 'elements/LinkButton'
import { Margin } from 'elements/Margin'
import { FilterItemLayout } from 'components/FilterItem/layout'
import filterReducer, { Comparisons, Filter, Group, Reducer, Rule, State } from './reducer'
import Popup from 'shared/components/Popup'
import { COMPARISONS } from 'components/FilterValuesPopover'
import { getRuleDiscriminator } from 'libs/filter-rule-discriminator'

const FILTER_COLOR = '#DEE6F0'

interface Option<T> {
  field: string
  label: string
  icon:
    | import('semantic-ui-react').SemanticICONS
    | 'far fa-screen-users'
    | 'far fa-user-group'
    | 'far fa-paper-plane'
    | 'far fa-chart-network'
    | 'far fa-globe'
  comparison: string
  filters?: Filter[]
  extra?: T
}

interface OnChangeValue<T> {
  globalOperator: string
  groups: Group<T>[]
}

interface FilterGroupProps<T> {
  options: Option<T>[]
  onChange: (filter: OnChangeValue<T>) => void
  defaultGlobalOperator: string
  defaultValue?: Group<T>[] | null
  _reducer?: Reducer<T>
  style?: React.CSSProperties
  comparisons?: Comparisons[]
  allowKeepingSelectedOption?: boolean
}

function FilterGroupV2<T>({
  options,
  onChange,
  defaultGlobalOperator,
  defaultValue,
  _reducer = filterReducer.reducer,
  comparisons = COMPARISONS,
  allowKeepingSelectedOption,
}: FilterGroupProps<T>) {
  const initialState = getInitialState(defaultGlobalOperator, defaultValue)
  const [state, dispatch] = useReducer(_reducer, initialState)

  const actions = filterReducer.createActions(dispatch)

  const { activeGroupIndex, activeRuleIndex, globalOperator, groups = [], isDropdownOpen } = state

  const [openDropdownGroupIndex, setOpenDropdownGroupIndex] = useState<number | null>(null)

  const usedFields = groups?.reduce<string[]>((acc, group) => {
    const fields = group.rules
      ?.map((rule) => (rule?.field ? rule.field + getRuleDiscriminator(rule) : undefined))
      .filter((field): field is string => field !== undefined)

    return acc.concat(fields)
  }, [])

  const optionsWithoutResource = R.reject(checkIsResource, options)

  const availableOptions = allowKeepingSelectedOption
    ? optionsWithoutResource
    : R.reject(fieldIn(usedFields), optionsWithoutResource)

  function handleAddFilter(newRule: Rule<T>) {
    if (activeGroupIndex !== undefined) {
      actions.addRule(newRule, activeGroupIndex)
      actions.openPopover(activeGroupIndex, activeRuleIndex)
    } else {
      actions.addRule(newRule)
      actions.openPopover(groups.length, 0)
    }
  }

  const atLeastOneRuleIsEmpty = Boolean(
    groups?.some((group) => group.rules?.some((rule) => rule?.filters?.length === 0))
  )

  const canAddMoreFilter = !atLeastOneRuleIsEmpty

  function handleOnChange(globalOperator: string, groupsArray: Group<T>[]) {
    if (shouldCallOnChange(groupsArray)) {
      const onChangeValue: OnChangeValue<T> = {
        globalOperator,
        groups: groupsArray.map((group) => ({
          operator: group.operator,
          rules: group.rules.map((rule) => ({
            field: rule?.field,
            label: rule?.label,
            icon: rule?.icon,
            comparison: rule?.comparison,
            filters: rule?.filters,
            extra: rule?.extra,
          })),
        })),
      }
      onChange(onChangeValue)
    }
  }

  return (
    <Fragment>
      <SmallerDropdown />
      <FilterItemLayout>
        {groups.map(({ rules = [] }, groupIndex) => (
          <div key={`group_${groupIndex}`} className="flex flex-wrap gap-y-[6px]">
            {groupIndex !== 0 && (
              <Margin right={0}>
                <AdvancedFilterOperatorButton
                  operator={globalOperator}
                  customStyles="mx-1"
                  onClick={() => {
                    const newGlobalOperator = globalOperator === 'and' ? 'or' : 'and'
                    actions.setGlobalOperator(newGlobalOperator)
                    handleOnChange(newGlobalOperator, [...state.groups])
                  }}
                />
              </Margin>
            )}
            {rules.map((rule, ruleIndex) => {
              return (
                <FeedbackItem
                  key={`rule_${ruleIndex}`}
                  rule={rule}
                  ruleIndex={ruleIndex}
                  groupIndex={groupIndex}
                  state={state}
                  actions={actions}
                  comparisons={comparisons}
                  onClosePopover={handleOnChange}
                />
              )
            })}
            <div className="relative inline-block">
              <StyledButton
                className={`flex h-full w-6 items-center justify-start ${
                  groups[groupIndex].rules.length > 1 ? '!pl-[0.33rem]' : '!px-1.5'
                }`}
                style={{
                  backgroundColor: FILTER_COLOR,
                }}
                attached="right"
                icon
                onClick={(e) => {
                  e.preventDefault()
                  actions.clickAddRule(groupIndex, groups[groupIndex].rules.length)
                  setOpenDropdownGroupIndex(
                    openDropdownGroupIndex === groupIndex ? null : groupIndex
                  )
                }}
              >
                <i className="inline-block w-auto ml-0 text-left text-[#9b9b9b] hover:text-[#4a4a4a] pt-[1px] icon far fa-plus" />
              </StyledButton>
              {openDropdownGroupIndex === groupIndex && (
                <FilterDropdown
                  open={true}
                  options={availableOptions}
                  onClose={() => {
                    setOpenDropdownGroupIndex(null)
                    actions.closeDropdown
                  }}
                  onFilterAdd={handleAddFilter}
                  style={{
                    top: '100%',
                    left: '0',
                    zIndex: 1000,
                  }}
                />
              )}
            </div>
          </div>
        ))}
      </FilterItemLayout>
      <InlineContainer data-html2canvas-ignore="true">
        <Popup
          basic
          tooltip="One of your filters is missing a value. Finish editing it before adding another."
          disabled={canAddMoreFilter}
        >
          <LinkButton
            onClick={(e) => {
              e.preventDefault()
              actions.clickAddGroup(groups.length, 0)
            }}
            disabled={!canAddMoreFilter}
            style={{ fontSize: '14px', height: 40 }}
          >
            <i className="icon far fa-plus" />
            Add filter
          </LinkButton>
        </Popup>
        <FilterDropdown
          open={isDropdownOpen}
          options={availableOptions}
          onBlur={actions.closeDropdown}
          onClose={() => {
            actions.closeDropdown
            actions.openPopover(activeGroupIndex, activeRuleIndex)
          }}
          onFilterAdd={handleAddFilter}
        />
      </InlineContainer>
    </Fragment>
  )
}

FilterGroupV2.defaultProps = {
  _reducer: filterReducer.reducer,
}

function getInitialState<T>(globalOperator, defaultValue?: Group<T>[] | null): State<T> {
  if (!defaultValue) {
    return filterReducer.initialState
  }
  return { ...filterReducer.initialState, globalOperator, groups: defaultValue }
}

function shouldCallOnChange<T>(groups: Group<T>[]): boolean {
  return groups.every((group) =>
    group.rules.every((rule) => {
      if (rule) {
        return (rule.filters || []).length > 0
      } else {
        return false
      }
    })
  )
}

function AdvancedFilterOperatorButton({ operator, onClick, customStyles = '', inlineStyles = {} }) {
  return (
    <button
      type="button"
      className={`w-8 pt-[8px] pb-[9.5px] border-none rounded shadow-none hover:shadow-none focus:shadow-none bg-white text-black text-[#9b9b9b] hover:text-[#4a4a4a] font-bold cursor-pointer flex justify-center items-center ${customStyles}`}
      style={{
        ...inlineStyles,
      }}
      onClick={onClick}
    >
      <strong>{operator}</strong>
    </button>
  )
}

function FeedbackItem<T>({
  rule,
  groupIndex,
  ruleIndex,
  state,
  actions,
  comparisons,
  onClosePopover,
}: {
  rule: any
  groupIndex: number
  ruleIndex: number
  state: State<T>
  actions: ReturnType<typeof filterReducer.createActions>
  comparisons: Comparisons[]
  onClosePopover: (globalOperator: string, groupsArray: Group<T>[]) => void
}) {
  const open = groupIndex === state.activeGroupIndex && ruleIndex === state.activeRuleIndex

  function handleFilterChange(rule) {
    actions.setFilter(groupIndex, ruleIndex, rule)
  }

  function handleComparisonChange(comparison) {
    actions.setComparison(groupIndex, ruleIndex, comparison)
  }

  function handleExtraChange(data) {
    actions.setExtra(groupIndex, ruleIndex, data)
  }

  function handleClick() {
    if (!open) {
      actions.openPopover(groupIndex, ruleIndex)
    }
  }

  function handleRemove() {
    actions.removeRule(groupIndex, ruleIndex)

    const newRules = R.remove(ruleIndex, 1, [...state.groups[groupIndex].rules])

    const updatedGroups = R.update(
      groupIndex,
      { ...state.groups[groupIndex], rules: newRules },
      state.groups
    )

    const filteredGroups = updatedGroups.filter((group) => group.rules.length > 0)

    onClosePopover(state.globalOperator, filteredGroups)
  }

  function handleClosePopover() {
    requestAnimationFrame(() => {
      actions.closePopover(groupIndex, ruleIndex)
      onClosePopover(state.globalOperator, state.groups)
    })
  }

  return (
    <>
      {ruleIndex !== 0 && (
        <AdvancedFilterOperatorButton
          operator={state.groups[groupIndex].operator}
          customStyles="!rounded-none"
          inlineStyles={{ backgroundColor: FILTER_COLOR }}
          onClick={() => {
            const currentOperator = state.groups[groupIndex].operator
            const newOperator = currentOperator === 'and' ? 'or' : 'and'
            const newGroup = [...state.groups]
            newGroup[groupIndex] = { ...newGroup[groupIndex], operator: newOperator }
            actions.setGroupOperator(groupIndex, newOperator)
            onClosePopover(state.globalOperator, newGroup)
          }}
        />
      )}
      <FilterItem
        rule={rule}
        open={open}
        onChangeValue={handleFilterChange}
        onClick={handleClick}
        onComparisonChange={handleComparisonChange}
        onExtraChange={handleExtraChange}
        onRemove={handleRemove}
        onPopoverClose={handleClosePopover}
        comparisons={comparisons}
        newAdvancedFilter={true}
        index={ruleIndex}
        rulesLength={state.groups[groupIndex].rules.length}
      />
    </>
  )
}

const InlineContainer = styled.div`
  display: inline-block;
`

const SmallerDropdown = createGlobalStyle`
  .ui.selection.dropdown > .menu > .item,
  .ui.selection.dropdown > .ui.label {
  font-size: 14px;
}
`

const StyledButton = styled(Button)`
  &&& {
    box-shadow: none !important;
  }
`
function fieldIn(rulesArray: string[]) {
  return (ruleFieldObj: Option<any>) =>
    !!R.find(R.equals(ruleFieldObj.field + getRuleDiscriminator(ruleFieldObj)), rulesArray)
}

function checkIsResource(option: Option<any>) {
  return R.includes(option.label, ['Resources', 'Resource'])
}

export default FilterGroupV2



// WEBPACK FOOTER //
// ./src/shared/components/FilterGroupV2/index.tsx